import { useEffect, useRef } from "react";
import { store, useTick } from "../../plc-sim/store";
import type { DeviceInst } from "../../plc-sim/types";
import motorSound from "../../assets/sounds/motor-fan-long.mp3";

const CLICK_SOUND = `${__BASE_PATH__.replace(/\/$/, "")}/plc-assets/click.mp3`;

function watchedState(d: DeviceInst): boolean | string | null {
  switch (d.type) {
    case "contactor": case "relay": case "timer": return Boolean(d.state["coil"]);
    case "pb_start": case "pb_stop": return Boolean(d.state["pressed"]);
    case "estop": return Boolean(d.state["latched"]);
    case "selector": return Boolean(d.state["man"]);
    case "mcb": case "mcb1": case "mcb3": return d.state["on"] !== false;
    case "overload": return Boolean(d.state["tripped"]);
    case "limit": case "prox": return Boolean(d.state["on"]);
    default: return null;
  }
}

function anyMotorRunning(): boolean {
  return store.project.devices.some(
    d => (d.type === "motor" || d.type === "motor_star_delta") && Boolean(d.state["run"]),
  );
}

export function SimulatorAudio() {
  const tick = useTick();
  const clickAudio = useRef<HTMLAudioElement | null>(null);
  const motorAudio = useRef<HTMLAudioElement | null>(null);
  const previous = useRef<Map<string, boolean | string | null>>(new Map());
  const initialized = useRef(false);
  const wasMotorRunning = useRef(false);

  useEffect(() => {
    const click = new Audio(CLICK_SOUND);
    click.preload = "auto";
    click.volume = 0.82;
    clickAudio.current = click;

    const motor = new Audio(motorSound);
    motor.preload = "auto";
    motor.loop = true;
    motor.volume = 0.68;
    motor.setAttribute("playsinline", "");
    motorAudio.current = motor;

    // iPhone/iPad browsers require one user gesture before later programmatic audio.
    // Prime ONLY the audio element; this never creates a link/download overlay.
    const unlock = () => {
      const oldMuted = motor.muted;
      motor.muted = true;
      const p = motor.play();
      if (p) void p.then(() => {
        motor.pause();
        motor.currentTime = 0;
        motor.muted = oldMuted;
        if (wasMotorRunning.current) void motor.play().catch(() => undefined);
      }).catch(() => { motor.muted = oldMuted; });
    };
    window.addEventListener("pointerdown", unlock, { capture: true, once: true });

    return () => {
      window.removeEventListener("pointerdown", unlock, true);
      click.pause();
      motor.pause();
      motor.currentTime = 0;
      clickAudio.current = null;
      motorAudio.current = null;
    };
  }, []);

  useEffect(() => {
    void tick;

    const running = anyMotorRunning();
    if (running !== wasMotorRunning.current) {
      wasMotorRunning.current = running;
      const motor = motorAudio.current;
      if (motor) {
        if (running) {
          motor.muted = false;
          motor.loop = true;
          motor.volume = 0.68;
          void motor.play().catch(() => undefined);
        } else {
          motor.pause();
          motor.currentTime = 0;
        }
      }
    }

    const next = new Map<string, boolean | string | null>();
    let shouldClick = false;
    for (const d of store.project.devices) {
      const value = watchedState(d);
      if (value === null) continue;
      next.set(d.id, value);
      if (initialized.current && previous.current.has(d.id) && previous.current.get(d.id) !== value) shouldClick = true;
    }
    previous.current = next;
    if (!initialized.current) { initialized.current = true; return; }

    if (shouldClick && clickAudio.current) {
      const clone = clickAudio.current.cloneNode(true) as HTMLAudioElement;
      clone.volume = clickAudio.current.volume;
      void clone.play().catch(() => undefined);
    }
  }, [tick]);

  return null;
}
